// AffiliateDashboard.tsx
import React, { useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import BasicTable from "@diditusa/shared/web_components/BasicTable/BasicTable";
import adminApi from "@diditusa/shared/api/adminApi";
import { Affiliate } from "@diditusa/shared/types/affiliateTypes";
import { BasicTableHeader } from "@diditusa/shared/types/commonTypes";
import LoadingIndicator from "@diditusa/shared/web_components/LoadingIndicator/loadingIndicator";
import { getErrorMessage } from "@diditusa/shared/api/apiUtils";
import { useNavigate } from "react-router-dom";

const AffiliateDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { data: affiliates, isLoading } = adminApi.useAdminGetAffiliatesQuery();
  const [createAffiliate, createAffiliateState] =
    adminApi.useAdminCreateAffiliateMutation();

  const [selected, setSelected] = useState<any | null>(null);
  const [openAdd, setOpenAdd] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phoneNumber: "" });

  useEffect(() => {
    if (createAffiliateState.isSuccess) {
      setOpenAdd(false);
      setForm({ name: "", email: "", phoneNumber: "" });
    }
  }, [createAffiliateState.isSuccess]);

  const handleAdd = async () => {
    await createAffiliate(form);
  };

  interface AffiliateWithUrl extends Affiliate {
    url: string;
  }
  type AffiliateKey = keyof AffiliateWithUrl;
  const headers: BasicTableHeader<AffiliateKey>[] = [
    { key: "name", value: "Name" },
    { key: "email", value: "Email" },
    { key: "token", value: "Token" },
    { key: "url", value: "Url" },
  ];

  return (
    <Box p={4}>
      <Typography variant="h4" gutterBottom>
        Affiliates
      </Typography>
      <Button
        variant="contained"
        onClick={() => setOpenAdd(true)}
        sx={{ mb: 2 }}
      >
        Add Affiliate
      </Button>
      <LoadingIndicator isVisible={isLoading} />

      {affiliates ? (
        <BasicTable
          headers={headers}
          data={
            affiliates.map((a) => ({
              ...a,
              url: `https://saef.app/invites/${a.token}`,
            })) || []
          }
          getCustomComponentForField={(field, idx) => {
            if (field === "token") {
              return <code>{affiliates[idx][field]}</code>;
            }

            if (field === "name") {
              return (
                <Button
                  variant="text"
                  onClick={() => navigate(`${affiliates[idx].id}`)}
                >
                  {affiliates[idx].name}
                </Button>
              );
            }
            return null;
          }}
        />
      ) : (
        <p>No affiliates found</p>
      )}

      <Dialog open={!!selected} onClose={() => setSelected(null)}>
        <DialogTitle>Affiliate Details</DialogTitle>
        <DialogContent>
          {selected && (
            <Box>
              <Typography>
                <strong>Name:</strong> {selected.name}
              </Typography>
              <Typography>
                <strong>Email:</strong> {selected.email}
              </Typography>
              <Typography>
                <strong>Phone:</strong> {selected.phoneNumber || "N/A"}
              </Typography>
              <Typography>
                <strong>Token:</strong> {selected.token}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openAdd} onClose={() => setOpenAdd(false)}>
        <DialogTitle>Add New Affiliate</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Phone Number"
            value={form.phoneNumber}
            onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAdd(false)}>Cancel</Button>
          <Button onClick={handleAdd} variant="contained">
            Add
            {createAffiliateState.isLoading && (
              <LoadingIndicator isVisible={createAffiliateState.isLoading} />
            )}
          </Button>
          {createAffiliateState.isError && (
            <p>{getErrorMessage(createAffiliateState.error)}</p>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AffiliateDashboard;
